import React from "react";
import { spring, useVideoConfig, interpolate } from "remotion";
import { BulletItem } from "../../types";
import { SvgIcon } from "../../components/SvgComponents";
import { HighlightText } from "../../components/HighlightText";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";


export const KineticTypeInsights: React.FC<{
  bullets: BulletItem[];
  globalFrame: number;
}> = ({ bullets, globalFrame }) => {
  const { fps, width: videoWidth } = useVideoConfig();

  return (
    <div
      style={{
        flex: 1,
        width: "100%",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {bullets.map((bullet, i) => {
        const startFrame = (bullet.startMs * fps) / 1000;
        const endFrame = (bullet.endMs * fps) / 1000;
        const relativeFrame = globalFrame - startFrame;

        // Show only the active insight with a small overlap for transitions
        if (globalFrame < startFrame - 20 || globalFrame > endFrame + 20) return null;

        // Entrance Animation (Slide from right)
        const entrance = spring({
          frame: relativeFrame,
          fps,
          config: { stiffness: 100, damping: 18, mass: 1 },
        });

        // Exit Animation (Slide to left)
        const exitFrame = globalFrame - (endFrame - 10);
        const exit = spring({
          frame: exitFrame,
          fps,
          config: { stiffness: 100, damping: 18, mass: 1.2 },
        });

        const slideX = interpolate(entrance, [0, 1], [videoWidth, 0]) +
                      interpolate(exit, [0, 1], [0, -videoWidth]);

        const opacity = interpolate(relativeFrame, [0, 10], [0, 1], { extrapolateRight: "clamp" }) *
                        interpolate(exitFrame, [0, 10], [1, 0], { extrapolateRight: "clamp" });

        const scale = interpolate(relativeFrame, [0, 15], [0.7, 1], { extrapolateRight: "clamp" });

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              width: "90%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "40px",
              opacity,
              transform: `translateX(${slideX}px) scale(${scale})`,
              padding: "50px 30px",
              background: "rgba(5, 8, 20, 0.7)",
              borderRadius: "40px",
              border: "2px solid rgba(34, 211, 238, 0.4)",
              backdropFilter: "blur(30px)",
              textAlign: "center",
              boxShadow: "0 30px 60px rgba(0, 0, 0, 0.6), 0 0 40px rgba(34, 211, 238, 0.15)",
            }}
          >
            {/* BIG Animated SVG Icon */}
            <div
              style={{
                width: "260px",
                height: "260px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "#BEF264",
                borderRadius: "70px",
                transform: `rotate(${Math.sin(globalFrame * 0.05) * 8}deg) translateY(${Math.sin(globalFrame * 0.1) * 12}px)`,
                boxShadow: "0 0 60px rgba(190, 242, 100, 0.6), inset 0 0 25px rgba(255, 255, 255, 0.6)",
              }}
            >
              {(() => {
                const brand = detectBrandInText(bullet.text);
                return brand ? (
                  <CompanyBadge
                    name={brand}
                    color="#050814"
                    accentColor="#BEF264"
                    size={180}
                    style="minimal"
                  />
                ) : (
                  <SvgIcon
                    name={bullet.icon || "sparkles"}
                    width={180}
                    height={180}
                    primaryColor="#050814"
                  />
                );
              })()}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "16px",
                }}
              >
                <div style={{ height: "3px", width: "40px", background: "linear-gradient(to right, transparent, #22D3EE)" }} />
                <span
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "26px",
                    fontWeight: 900,
                    color: "#22D3EE",
                    textTransform: "uppercase",
                    letterSpacing: "8px",
                  }}
                >
                  STEP {i + 1}
                </span>
                <div style={{ height: "3px", width: "40px", background: "linear-gradient(to left, transparent, #22D3EE)" }} />
              </div>

              <p
                style={{
                  fontFamily: "Outfit, sans-serif",
                  fontSize: "56px",
                  fontWeight: 900,
                  color: "white",
                  margin: 0,
                  lineHeight: 1.1,
                  textShadow: "0 4px 15px rgba(0,0,0,0.5)",
                }}
              >
                <HighlightText
                  text={bullet.text}
                  highlights={bullet.highlights}
                  highlightStyle={{ color: "#BEF264", display: "inline" }}
                />
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
